import React, {useState, useCallback, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import Swal from 'sweetalert2'
import PortfolioContainer from './PortfolioContainer'
import {getDownloadLink} from './core/_requests'
import {initialQueryRequest} from './core/crud-helper'
import {PageTitle} from '../../../_metronic/layout/core'

const PortfolioPreviewPage: React.FC = () => {
  const [isExportLoading, setIsExportLoading] = useState(false)
  const [downloadLink, setDownloadLink] = useState('')

  const location = useLocation()
  const projectIds = React.useMemo(() => {
    // First try to get from session storage (set by PortFolioWrapper)
    const storedProjects = sessionStorage.getItem('previewProjects')
    if (storedProjects) {
      return JSON.parse(storedProjects)
    }

    // Fall back to URL parameters for direct access
    const queryParams = new URLSearchParams(location.search)
    return queryParams.get('projects')?.split(',') || []
  }, [location.search])

  const showToast = useCallback((message: string, isError = false) => {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: isError ? 'error' : 'success',
      title: message,
      showConfirmButton: false,
      timer: 3000,
    })
  }, [])

  useEffect(() => {
    // Hide the global splash screen loader
    const splashScreen = document.getElementById('splash-screen')
    if (splashScreen) {
      splashScreen.style.display = 'none'
    }

    // Remove page-loading class from body
    document.body.classList.remove('page-loading')

    document.title = 'Preview Portfolio'
  }, [])

  const onGenerateDownloadLink = useCallback(async () => {
    setIsExportLoading(true)

    try {
      // Use existing API with isExport: false to generate token
      const payload = {
        ...initialQueryRequest.state,
        filter: {
          project: projectIds,
        },
        isExport: false,
      }

      const response = await getDownloadLink(payload)

      if (response.token) {
        const baseUrl = window.location.origin
        const publicUrl = `${baseUrl}/sharable/${response.token}`

        setDownloadLink(publicUrl)
        showToast('Public link generated')

        // Handle clipboard copy with separate error handling
        try {
          await navigator.clipboard.writeText(publicUrl)
        } catch (clipboardError: any) {
          console.warn('Clipboard copy failed, but link was generated:', clipboardError)
          // Don't show error toast for clipboard failures as the main operation succeeded
        }

        // Handle window.open with separate error handling
        try {
          const newWindow = window.open(publicUrl, '_blank')
          // Check if window was actually opened (Safari returns null if blocked)
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            throw new Error('Popup blocked')
          }
        } catch (windowError: any) {
          console.warn('Popup blocked by browser, showing user instructions:', windowError)
          // Show user-friendly message for Safari users
          showToast('Link copied! Please paste in a new tab to open.', false)
        }
      } else {
        throw new Error('No token received from server')
      }
    } catch (error: any) {
      console.error('Link generation failed', error)
      // Only show error toast if the actual link generation failed, not clipboard/popup issues
      if (error.message === 'No token received from server' || 
          (!error.message?.includes('clipboard') && !error.message?.includes('popup'))) {
        showToast('Failed to generate link', true)
      }
    } finally {
      setIsExportLoading(false)
    }
  }, [projectIds, showToast])

  if (projectIds?.length === 0) {
    return (
      <div className='d-flex align-items-center justify-content-center vh-100'>
        <div className='alert alert-warning'>No projects selected for preview.</div>
      </div>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Preview Portfolio</PageTitle>
      <PortfolioContainer
        projectIds={projectIds}
        onGenerateLink={onGenerateDownloadLink}
        isExportLoading={isExportLoading}
        downloadLink={downloadLink}
        isPublic={true}
      />
    </>
  )
}

export default PortfolioPreviewPage
